import React, { useState } from "react";
import { Modal, Input, Button, message, Alert } from "antd";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

const DeleteUserModal = ({ open, onClose, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const handleDelete = async () => {
    if (!email || !email.trim()) {
      message.error("Please enter a user email");
      return;
    }

    if (!email.includes("@")) {
      message.error("Please enter a valid email address");
      return;
    }

    if (confirmText !== "DELETE") {
      message.error('Please type "DELETE" to confirm');
      return;
    }

    setLoading(true);

    try {
      // Import the delete function
      const { hardDeleteUser } = await import("../../api/userManagementApi");

      const result = await hardDeleteUser(email);

      message.success(
        `User ${email} has been permanently deleted from the system`
      );

      // Reset form
      setEmail("");
      setConfirmText("");

      // Call success callback
      if (onSuccess) {
        onSuccess(result);
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error deleting user:", error);
      message.error(
        error.message || "Failed to delete user. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEmail("");
    setConfirmText("");
    onClose();
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <DeleteOutlined style={{ color: "#ff4d4f" }} />
          <span>Hard Delete User</span>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="delete"
          type="primary"
          danger
          loading={loading}
          onClick={handleDelete}
          disabled={!email || confirmText !== "DELETE"}
        >
          Permanently Delete User
        </Button>,
      ]}
      width={600}
    >
      <div className="flex flex-col gap-4 py-4">
        <Alert
          message="Warning: This action is irreversible!"
          description="Hard delete will permanently remove the user from both Firestore and Firebase Authentication. This action cannot be undone."
          type="error"
          icon={<ExclamationCircleOutlined />}
          showIcon
        />

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">
            User Email <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter user email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            size="large"
          />
          <span className="text-xs text-gray-500">
            Enter the email address of the user you want to delete
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">
            Type "DELETE" to confirm <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder='Type "DELETE" to confirm'
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={loading}
            size="large"
          />
          <span className="text-xs text-gray-500">
            This extra step ensures you understand this action is permanent
          </span>
        </div>

        <Alert
          message="What will be deleted:"
          description={
            <ul className="list-disc pl-5 mt-2 text-sm">
              <li>User account from Firebase Authentication</li>
              <li>User document from Firestore database</li>
            </ul>
          }
          type="warning"
          showIcon
        />
      </div>
    </Modal>
  );
};

export default DeleteUserModal;
